import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, shareReplay, take } from 'rxjs/operators';
import { EMPTY } from 'rxjs';
import { Block } from '@core/models/block/block.model';
import { BlockActionModel } from '@core/models';
import { MixedObject } from '@core/interfaces/mixed-object';
import { ApiBlockService } from '@core/services/api/api-block.service';
import { StyleService } from '@core/services/common/styles/style.service';
import { MediaQueryBuilder } from '@core/services/common/styles/media-query-builder';

/**
 * Kanał podglądu „na ścianie" — wzorzec popupa (AT-9277). Akcja VIEW_ON_WALL niesie
 * action.actionData.blockId wybranego bloku wall-preview; dociągamy go po id z kontekstem
 * pracy (model) i pushujemy do outletu. Style scoped do uId rejestrujemy jak popup.
 */
@Injectable({ providedIn: 'root' })
export class WallPreviewService {
  private readonly _block$ = new Subject<Block>();
  /**
   * AT-9354: blok popupu dociągany z wyprzedzeniem (hover/focus/pointerdown na przycisku), żeby
   * po kliknięciu fade-in ruszał od razu, a nie po zapytaniu do API. Wpis zużywamy przy otwarciu —
   * kolejne otwarcie dociąga świeży blok (komponent może go modyfikować).
   */
  private readonly prefetched = new Map<string, Observable<Block>>();
  readonly block$: Observable<Block> = this._block$.asObservable().pipe(distinctUntilChanged());

  constructor(
    private api: ApiBlockService,
    private styleService: StyleService,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  prefetch(actionData: MixedObject, model: BlockActionModel): void {
    const key = this.cacheKey(actionData, model);
    if (!actionData?.blockId || this.prefetched.has(key)) {
      return;
    }
    const block$ = this.api.get(actionData.blockId, true, model).pipe(
      take(1),
      // Nieudany prefetch nie może zablokować otwarcia — kliknięcie spróbuje ponownie.
      catchError(() => {
        this.prefetched.delete(key);
        return EMPTY;
      }),
      shareReplay(1),
    );
    this.prefetched.set(key, block$);
    block$.subscribe();
  }

  openModal(actionData: MixedObject, model: BlockActionModel): void {
    const key = this.cacheKey(actionData, model);
    const block$ = this.prefetched.get(key) ?? this.api.get(actionData.blockId, true, model);
    this.prefetched.delete(key);
    block$
      .pipe(take(1))
      .subscribe((block) => {
        // Praca bez rozwiniętych renderów (backend zwraca interiors: [] → renderList
        // sklonowany do zera dzieci) — stary WallPreviewComponent.openPreview() milczał
        // (`if (!this.renderBlocks.length) return;`); tu, przed pushem do outletu, musimy
        // powtórzyć tę samą decyzję, inaczej otworzy się pusty, zablokowany na scroll modal
        // (AT-9277 regresja — stage jest za *ngIf="currentInterior", lista za renderBlocks.length).
        if (!this.hasRenders(block)) {
          return;
        }
        this.createStyles(block);
        this._block$.next(block);
        this.document.body.style.overflow = 'hidden';
      });
  }

  close(): void {
    this._block$.next(null);
    this.document.body.style.removeProperty('overflow');
  }

  private cacheKey(actionData: MixedObject, model: BlockActionModel): string {
    return `${actionData?.blockId}|${model?.className ?? ''}|${model?.id ?? ''}`;
  }

  private createStyles(block: Block): void {
    StyleService.bodyStyles.push(MediaQueryBuilder.build([block]));
    this.styleService.setStyles();
  }

  /** Czy blok ma choć jeden render do pokazania — mirror WallPreviewComponent.findByViewPart. */
  private hasRenders(block: Block | null | undefined): boolean {
    const renderList = this.findByViewPart('renderList', block);
    return !!renderList?.children?.length;
  }

  private findByViewPart(viewPart: string, root: Block | null | undefined): Block | null {
    if (!root) {
      return null;
    }
    if (root.settings?.viewPart === viewPart) {
      return root;
    }
    for (const child of root.children ?? []) {
      const hit = this.findByViewPart(viewPart, child);
      if (hit) {
        return hit;
      }
    }
    return null;
  }
}
